import React, { useState, useEffect } from "react";
import { Container, Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Add as AddIcon } from "@mui/icons-material";

import NavigationHeader from "../../../components/NavigationHeader";
import { Loading } from "../../../components/Loading";
import { useToast } from "../../../components/Toast";
import DeleteConfirmationDialog from "../../../components/DeleteConfirmationDialog";

import ModuleFilters from "../components/ModuleFilters";
import ModuleListView from "../components/ModuleListView";
import ModuleActionModal from "../components/ModuleActionModal";
import ProjectModulesSkeleton from "../components/skeletons/ProjectModulesSkeleton";

import { ProjectModule, ProjectFilter, CreateProjectRequest } from "../types";
import { projectModuleApi } from "../services/apiService";
import { PROJECT_TERMINOLOGY } from "../enums";
import { usePlanStatus } from "../../../hooks/usePlanStatus";
import { UserScope } from "../../../types/operationType";

const ProjectModules: React.FC = () => {
  const navigate = useNavigate();

  const { planStatus, loading: planStatusLoading } = usePlanStatus();

  const [modules, setModules] = useState<ProjectModule[]>([]);
  const [filters, setFilters] = useState<ProjectFilter>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Modal states
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  // Selected items
  const [selectedModule, setSelectedModule] = useState<ProjectModule | null>(
    null
  );
  const [moduleToDelete, setModuleToDelete] = useState<string | null>(null);

  const { showToast } = useToast();

  // Fetch modules
  const fetchModules = async () => {
    setIsLoading(true);
    try {
      let data;
      if (planStatus.user?.userRole?.scope === UserScope.GLOBAL) {
        data = await projectModuleApi.getModules(filters);
      } else {
        data = await projectModuleApi.getAssignedModules(filters);
      }
      setModules(data);
    } catch (error) {
      showToast("Something went wrong", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Initial data load
  useEffect(() => {
    if (!planStatusLoading) {
      fetchModules();
    }
  }, [filters, planStatusLoading]);

  // Handlers
  const handleAddModule = () => {
    setSelectedModule(null);
    setActionModalOpen(true);
  };

  const handleEditModule = (module: ProjectModule) => {
    setSelectedModule(module);
    setActionModalOpen(true);
  };

  const handleViewModule = (module: ProjectModule) => {
    navigate(`/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/${module.id}`);
  };

  const handleDeleteModule = (id: string) => {
    setModuleToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleSubmitModule = async (data: CreateProjectRequest) => {
    setIsSubmitting(true);
    try {
      if (selectedModule) {
        await projectModuleApi.updateModule({ ...data, id: selectedModule.id });
        showToast(
          `${PROJECT_TERMINOLOGY.SINGULAR} updated successfully`,
          "success"
        );
      } else {
        const newModule = await projectModuleApi.createModule(data);
        navigate(`/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/${newModule.id}`);
        showToast(
          `${PROJECT_TERMINOLOGY.SINGULAR} created successfully`,
          "success"
        );
      }
      fetchModules();
      setActionModalOpen(false);
    } catch (error) {
      showToast(
        `Failed to save ${PROJECT_TERMINOLOGY.LOWER_SINGULAR}`,
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!moduleToDelete) return;

    setIsSubmitting(true);
    try {
      await projectModuleApi.deleteModule(moduleToDelete);
      showToast(
        `${PROJECT_TERMINOLOGY.SINGULAR} deleted successfully`,
        "success"
      );
      fetchModules();
    } catch (error) {
      showToast(
        `Failed to delete ${PROJECT_TERMINOLOGY.LOWER_SINGULAR}`,
        "error"
      );
    } finally {
      setIsSubmitting(false);
      setDeleteDialogOpen(false);
      setModuleToDelete(null);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <NavigationHeader
        title={PROJECT_TERMINOLOGY.PLURAL}
        button={
          planStatus.user?.userRole?.scope === UserScope.GLOBAL
            ? {
                label: `Add ${PROJECT_TERMINOLOGY.SINGULAR}`,
                onClick: handleAddModule,
                icon: <AddIcon />,
              }
            : null
        }
        backNavigationEnabled={false}
      />

      {/* Filters */}
      <ModuleFilters filters={filters} onFiltersChange={setFilters} />

      {/* List View */}
      <Box sx={{ position: "relative" }}>
        {isLoading && modules.length > 0 && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "rgba(255,255,255,0.7)",
            }}
          >
            <Loading
              message={`Updating ${PROJECT_TERMINOLOGY.LOWER_PLURAL}...`}
              fullScreen={false}
            />
          </Box>
        )}

        {isLoading && modules.length === 0 ? (
          <ProjectModulesSkeleton />
        ) : (
          <ModuleListView
            modules={modules}
            onView={handleViewModule}
            onEdit={handleEditModule}
            onDelete={handleDeleteModule}
            userScope={planStatus.user?.userRole?.scope}
          />
        )}
      </Box>

      {/* Modals */}
      <ModuleActionModal
        open={actionModalOpen}
        onClose={() => setActionModalOpen(false)}
        module={selectedModule}
        onSubmit={handleSubmitModule}
        isSubmitting={isSubmitting}
      />

      <DeleteConfirmationDialog
        deleteModalOpen={deleteDialogOpen}
        handleNo={() => setDeleteDialogOpen(false)}
        handleYes={handleConfirmDelete}
        itemToDelete={moduleToDelete}
        title={`Delete ${PROJECT_TERMINOLOGY.SINGULAR}`}
        message={`Are you sure you want to delete this ${PROJECT_TERMINOLOGY.LOWER_SINGULAR}? This action cannot be undone.`}
      />
    </Container>
  );
};

export default ProjectModules;
